import { useLayoutEffect, useRef } from "react";
import type { ColumnPinningState, RowData } from "@tanstack/react-table";
import {
    type DataGridLeafColumnInfo,
    getLeafColumnInfos,
} from "../columns/columnLayout";
import type { DataGridColumn } from "../columns/types";
import type { DataGridReactTable } from "../types";

/**
 * Pinning with the columns that weren't there before pinned per `meta.pin`
 * (report periods load after mount, so their Total arrives late).
 * `undefined` when no added column pins.
 */
export const pinAddedColumns = ({
    pinning,
    leaves,
    knownIds,
}: {
    pinning: ColumnPinningState;
    leaves: readonly DataGridLeafColumnInfo[];
    knownIds: ReadonlySet<string>;
}): ColumnPinningState | undefined => {
    const addedLeaves = leaves.filter(
        (leaf) =>
            leaf.pin !== undefined &&
            !knownIds.has(leaf.id) &&
            !pinning.start?.includes(leaf.id) &&
            !pinning.end?.includes(leaf.id),
    );
    if (addedLeaves.length === 0) {
        return undefined;
    }
    const idsFor = (pin: "start" | "end") =>
        addedLeaves.filter((leaf) => leaf.pin === pin).map(({ id }) => id);
    return {
        start: [...(pinning.start ?? []), ...idsFor("start")],
        end: [...(pinning.end ?? []), ...idsFor("end")],
    };
};

/** Seeds `meta.pin` for columns added after mount; existing columns keep the live pinning. */
export const useAddedColumnPins = <TData extends RowData>({
    table,
    columns,
}: {
    table: DataGridReactTable<TData>;
    columns: ReadonlyArray<DataGridColumn<TData>>;
}) => {
    const knownIdsRef = useRef<ReadonlySet<string> | null>(null);
    useLayoutEffect(() => {
        const leaves = getLeafColumnInfos(columns);
        const knownIds = knownIdsRef.current;
        knownIdsRef.current = new Set(leaves.map(({ id }) => id));
        if (knownIds === null) {
            return;
        }
        const next = pinAddedColumns({
            pinning: table.atoms.columnPinning.get(),
            leaves,
            knownIds,
        });
        if (next) {
            table.setColumnPinning(next);
        }
    }, [columns, table]);
};
